import {
  useState,
  useRef,
  useCallback,
  useLayoutEffect,
  useEffect,
  type RefObject,
  type SyntheticEvent,
} from 'react';

export interface PictureImageUrls {
  webp: string;
  jpg: string;
}

export function appendRetryParam(url: string, count: number): string {
  if (count <= 0) {
    return url;
  }
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}retry=${count}`;
}

export interface UsePictureImageLoadOptions {
  /** When false, skip reset and cached-image checks (e.g. while async ID resolution is pending). */
  enabled?: boolean;
  maxRetries?: number;
  /** When true (default), observe viewport visibility to detect lazy-loaded images finishing without onLoad. */
  lazy?: boolean;
  /** Called once when all retry attempts are exhausted. */
  onLoadFailed?: (failedSrc: string) => void;
}

export interface UsePictureImageLoadResult {
  imgRef: RefObject<HTMLImageElement | null>;
  isLoading: boolean;
  failedUrl: string | null;
  imageUrls: PictureImageUrls | null;
  pictureKey: string;
  handleLoad: () => void;
  handleError: (e: SyntheticEvent<HTMLImageElement, Event>) => void;
}

const LAZY_LOAD_POLL_MAX_FRAMES = 120;
const LAZY_LOAD_ROOT_MARGIN = '200px 0px';
const DEFAULT_MAX_RETRIES = 5;
const RETRY_BASE_DELAY_MS = 400;

/**
 * Shared loading state for <picture> image components.
 *
 * Handles retry cache-busting, loading overlays, and browsers (notably mobile Safari)
 * that serve cached or lazy-loaded images without firing onLoad.
 */
export function usePictureImageLoad(
  baseUrls: PictureImageUrls | null,
  identityKey: string,
  options: UsePictureImageLoadOptions = {},
): UsePictureImageLoadResult {
  const enabled = options.enabled ?? true;
  const maxRetries = options.maxRetries ?? DEFAULT_MAX_RETRIES;
  const lazy = options.lazy ?? true;
  const onLoadFailed = options.onLoadFailed;

  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  const imgRef = useRef<HTMLImageElement>(null);
  const retryCountRef = useRef(retryCount);
  const revealingRef = useRef(false);
  retryCountRef.current = retryCount;

  const waitForImageReady = useCallback((img: HTMLImageElement): Promise<void> => {
    if (typeof img.decode === 'function') {
      return img.decode().then(() => undefined).catch(() => undefined);
    }
    return Promise.resolve();
  }, []);

  const finishLoading = useCallback((img: HTMLImageElement) => {
    if (revealingRef.current) {
      return;
    }
    revealingRef.current = true;
    void waitForImageReady(img).finally(() => {
      revealingRef.current = false;
      setIsLoading(false);
    });
  }, [waitForImageReady]);

  const imageUrls = baseUrls
    ? {
        webp: appendRetryParam(baseUrls.webp, retryCount),
        jpg: appendRetryParam(baseUrls.jpg, retryCount),
      }
    : null;

  const pictureKey = imageUrls ? `${imageUrls.webp}-${imageUrls.jpg}` : identityKey;

  useLayoutEffect(() => {
    if (!enabled) {
      return;
    }
    setFailedUrl(null);
    setRetryCount(0);
    setIsLoading(true);
    revealingRef.current = false;
  }, [identityKey, enabled]);

  const markLoadFailed = useCallback(
    (failedSrc: string) => {
      setFailedUrl(failedSrc);
      setIsLoading(false);
      onLoadFailed?.(failedSrc);
    },
    [onLoadFailed],
  );

  const scheduleRetry = useCallback(() => {
    const currentRetry = retryCountRef.current;
    if (currentRetry >= maxRetries) {
      const failedSrc = imgRef.current?.src ?? '';
      markLoadFailed(failedSrc);
      return false;
    }

    setTimeout(() => {
      setRetryCount((count) => count + 1);
      setIsLoading(true);
    }, RETRY_BASE_DELAY_MS * (currentRetry + 1));
    return true;
  }, [markLoadFailed, maxRetries]);

  const revealImage = useCallback(() => {
    const img = imgRef.current;
    if (!img?.complete || img.naturalWidth === 0) {
      return;
    }
    finishLoading(img);
  }, [finishLoading]);

  const clearLoadingIfImageReady = useCallback(() => {
    revealImage();
  }, [revealImage]);

  const handleStuckLoad = useCallback(() => {
    const img = imgRef.current;
    if (!img?.complete || img.naturalWidth > 0) {
      return;
    }
    scheduleRetry();
  }, [scheduleRetry]);

  useLayoutEffect(() => {
    if (!enabled || !imageUrls) {
      return;
    }
    clearLoadingIfImageReady();
  }, [identityKey, pictureKey, clearLoadingIfImageReady, enabled, imageUrls]);

  useEffect(() => {
    if (!enabled || !imageUrls || !isLoading) {
      return;
    }

    const img = imgRef.current;
    if (!img) {
      return;
    }

    let rafId = 0;
    let pollCount = 0;

    const stopPolling = () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = 0;
      }
      pollCount = 0;
    };

    const startPolling = () => {
      stopPolling();
      const poll = () => {
        const current = imgRef.current;
        if (!current || pollCount >= LAZY_LOAD_POLL_MAX_FRAMES) {
          stopPolling();
          return;
        }
        pollCount += 1;
        clearLoadingIfImageReady();
        if (current.complete && current.naturalWidth > 0) {
          stopPolling();
          return;
        }
        if (current.complete && current.naturalWidth === 0) {
          handleStuckLoad();
          stopPolling();
          return;
        }
        rafId = requestAnimationFrame(poll);
      };
      rafId = requestAnimationFrame(poll);
    };

    const onNativeLoad = () => {
      clearLoadingIfImageReady();
    };

    img.addEventListener('load', onNativeLoad);

    let observer: IntersectionObserver | undefined;
    if (lazy) {
      observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            clearLoadingIfImageReady();
            handleStuckLoad();
            startPolling();
          } else {
            stopPolling();
          }
        },
        { rootMargin: LAZY_LOAD_ROOT_MARGIN },
      );
      observer.observe(img);
    } else {
      startPolling();
    }

    return () => {
      img.removeEventListener('load', onNativeLoad);
      observer?.disconnect();
      stopPolling();
    };
  }, [enabled, imageUrls, isLoading, lazy, clearLoadingIfImageReady, handleStuckLoad, pictureKey]);

  const handleLoad = useCallback(() => {
    const img = imgRef.current;
    if (img) {
      finishLoading(img);
    } else {
      setIsLoading(false);
    }
  }, [finishLoading]);

  const handleError = useCallback(
    (e: SyntheticEvent<HTMLImageElement, Event>) => {
      if (scheduleRetry()) {
        return;
      }
      markLoadFailed(e.currentTarget.src);
    },
    [markLoadFailed, scheduleRetry],
  );

  return {
    imgRef,
    isLoading,
    failedUrl,
    imageUrls,
    pictureKey,
    handleLoad,
    handleError,
  };
}
